import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";

export interface Readout {
  key: string;
  value: string;
  tone?: "default" | "bad" | "warn";
}

/**
 * The Direction A graft: a stronger RISE Navy contextual frame used once the
 * DM enters a deep operational context (Site → Unit, Site → Situation,
 * Site → Inspection, …) so they never lose track of where they are. This is
 * NOT used on the shallow list screens (Today/Units/Work/Inbox) — see
 * docs/ff01/FF01_VISUAL_IMPLEMENTATION.md "When the navy frame appears".
 */
export function ContextHeader({
  crumb,
  title,
  subtitle,
  readouts,
  onBack,
  right
}: {
  crumb: string;
  title: string;
  subtitle?: string;
  readouts?: Readout[];
  onBack?: boolean;
  right?: ReactNode;
}) {
  const navigate = useNavigate();
  return (
    <header className="bg-brand-navy px-5 pb-4 pt-4 text-white">
      <div className="flex items-center justify-between font-mono text-[11px] tracking-wide text-navyFgMuted">
        <div className="flex items-center gap-2">
          {onBack && (
            <button type="button" onClick={() => navigate(-1)} aria-label="Back" className="touch-target -ml-2 flex items-center justify-center text-white">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 6 9 12 15 18" />
              </svg>
            </button>
          )}
          <span>{crumb}</span>
        </div>
        {right}
      </div>
      <h1 className="mt-1.5 font-display text-[22px] leading-7 tracking-tight">{title}</h1>
      {subtitle && <p className="mt-1 text-[13px] leading-5 text-navyFgMuted">{subtitle}</p>}
      {readouts && readouts.length > 0 && (
        <div className="mt-3.5 grid grid-flow-col auto-cols-fr divide-x divide-navyBorder overflow-hidden rounded-sm border border-navyBorder">
          {readouts.map((r) => (
            <div key={r.key} className="px-3 py-2">
              <div className="font-mono text-[9px] tracking-wide text-navyFgMuted">{r.key}</div>
              <div
                className={`mt-0.5 font-mono text-[15px] font-semibold ${
                  r.tone === "bad" ? "text-[#FF9E93]" : r.tone === "warn" ? "text-[#FFC66B]" : "text-white"
                }`}
              >
                {r.value}
              </div>
            </div>
          ))}
        </div>
      )}
    </header>
  );
}
